import {
  Button,
  Checkbox,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Hotel, Plus, Search, X } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { Spinner } from '../../../../components';
import { useHotelOptions } from '../../hooks';

interface HotelPickerProps {
  value: string[];
  /** Dostaje CAŁĄ nową listę hoteli, nie deltę — tak też przyjmuje ją backend. */
  onChange: (hotelIds: string[]) => void;
  isSaving?: boolean;
}

const normalize = (value: string): string => value.toLowerCase();

/**
 * Wybór hoteli, którym wyświetlamy reklamę. Zaznaczenie potwierdza się
 * przyciskiem w modalu, bo endpoint przyjmuje stan docelowy listy — zapis po
 * każdym kliknięciu to tyle samo nadpisań `assigned_hotels`, z ryzykiem, że
 * wyścig dwóch requestów cofnie wcześniejszy wybór.
 */
export const HotelPicker: FC<HotelPickerProps> = ({ value, onChange, isSaving }) => {
  const { hotels, hotelNameById, isLoading } = useHotelOptions();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(value));

  useEffect(() => {
    if (isOpen) setSelected(new Set(value));
    else setSearch('');
  }, [isOpen, value]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return hotels;
    return hotels.filter((hotel) => [hotel.name, hotel.city, hotel.code, hotel.id]
      .some((field) => normalize(field).includes(needle)));
  }, [hotels, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((h) => selected.has(h.id));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((h) => (allFilteredSelected ? next.delete(h.id) : next.add(h.id)));
      return next;
    });
  };

  const handleConfirm = () => {
    onChange(Array.from(selected));
    setIsOpen(false);
  };

  return (
    <>
      {value.length === 0 ? (
        <p className="text-sm text-gray-400 italic py-2">
          Reklama nie jest jeszcze przypisana do żadnego hotelu — nikt jej nie zobaczy.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5 py-2">
          {value.map((hotelId) => (
            <Chip
              key={hotelId}
              size="sm"
              variant="flat"
              color="primary"
              startContent={<Hotel size={10} />}
              endContent={(
                <button
                  type="button"
                  onClick={() => onChange(value.filter((id) => id !== hotelId))}
                  className="ml-1 hover:opacity-70"
                  aria-label="Zdejmij reklamę z tego hotelu"
                  disabled={isSaving}
                >
                  <X size={11} />
                </button>
              )}
            >
              {hotelNameById.get(hotelId) || hotelId}
            </Chip>
          ))}
        </div>
      )}

      <Button
        size="sm"
        variant="flat"
        color="primary"
        startContent={<Plus size={14} />}
        onPress={() => setIsOpen(true)}
        isDisabled={isSaving}
        className="mt-2"
      >
        Przypisz hotele
      </Button>

      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => setIsOpen(open)}
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <span>Przypisz hotele</span>
                <span className="text-xs font-normal text-gray-500">
                  Reklama trafi do zaznaczonych hoteli pod tym samym id dokumentu.
                </span>
              </ModalHeader>
              <ModalBody>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <Input
                    size="sm"
                    placeholder="Szukaj po nazwie, mieście, kodzie..."
                    value={search}
                    onValueChange={setSearch}
                    startContent={<Search size={14} className="text-gray-400" />}
                    isClearable
                    onClear={() => setSearch('')}
                    classNames={{ base: 'flex-1 min-w-[240px]' }}
                  />
                  <Chip size="sm" variant="flat" color="primary">
                    Wybrano:
                    {' '}
                    {selected.size}
                  </Chip>
                </div>

                {isLoading ? (
                  <div className="py-8 flex justify-center">
                    <Spinner showLabel={false} />
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr className="text-left">
                          <th className="px-3 py-2 w-10">
                            <Checkbox
                              isSelected={allFilteredSelected}
                              isIndeterminate={
                                !allFilteredSelected && filtered.some((h) => selected.has(h.id))
                              }
                              onValueChange={toggleAllFiltered}
                              aria-label="Zaznacz wszystkie"
                            />
                          </th>
                          <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Hotel</th>
                          <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Miasto</th>
                          <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Kod</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                              Brak hoteli
                            </td>
                          </tr>
                        )}
                        {filtered.map((hotel) => {
                          const isSelected = selected.has(hotel.id);
                          return (
                            <tr
                              key={hotel.id}
                              className={`border-t border-gray-100 cursor-pointer transition-colors ${
                                isSelected ? 'bg-primary/5' : 'hover:bg-gray-50'
                              }`}
                              onClick={() => toggle(hotel.id)}
                            >
                              {/* Kliknięcie w checkbox NIE może dojść do wiersza:
                                  oba wołają `toggle`, więc bąbelkowanie przełączałoby
                                  zaznaczenie dwa razy i znosiło je do zera. */}
                              <td
                                className="px-3 py-2"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <Checkbox
                                  isSelected={isSelected}
                                  onValueChange={() => toggle(hotel.id)}
                                  aria-label={`Zaznacz ${hotel.name}`}
                                />
                              </td>
                              <td className="px-3 py-2 font-medium text-gray-900">{hotel.name}</td>
                              <td className="px-3 py-2 text-gray-600">{hotel.city || '—'}</td>
                              <td className="px-3 py-2">
                                {hotel.code ? (
                                  <Chip size="sm" variant="flat">{hotel.code}</Chip>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={() => setIsOpen(false)}>
                  Anuluj
                </Button>
                <Button color="primary" onPress={handleConfirm}>
                  Zapisz przypisanie
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
